// src\components\common\ResourceStatusPanel.jsx
import React from 'react';
import { Activity, AlertTriangle, CheckCircle } from 'lucide-react';

/**
 * Composant pour afficher l'état actuel des moyens chauds
 * @param {Object} props
 * @param {Array} props.resources - Liste des ressources
 * @param {Array} props.currentBatches - Fournées actuellement en cours
 */
const ResourceStatusPanel = ({ resources, currentBatches }) => {
  // Fonction pour obtenir la classe de couleur en fonction du statut
  const getStatusColorClass = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Fonction pour obtenir l'icône en fonction du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircle size={18} className="text-green-500" />;
      case 'in-progress':
        return <Activity size={18} className="text-blue-500" />;
      case 'maintenance':
        return <AlertTriangle size={18} className="text-yellow-500" />;
      case 'error':
        return <AlertTriangle size={18} className="text-red-500" />;
      default:
        return null;
    }
  };

  // Fonction pour obtenir le libellé du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case 'available':
        return 'Disponible';
      case 'in-progress':
        return 'En cours d\'utilisation';
      case 'maintenance':
        return 'En maintenance';
      case 'error':
        return 'Erreur détectée';
      default:
        return 'Statut inconnu';
    }
  };

  // Déterminer pour chaque ressource si elle est actuellement utilisée
  const resourcesWithStatus = resources.map(resource => {
    const currentBatch = currentBatches.find(batch => batch.resourceId === resource.id);
    
    // Si une fournée est en cours, la ressource est "in-progress"
    const effectiveStatus = currentBatch ? 'in-progress' : resource.status;
    
    return {
      ...resource,
      effectiveStatus,
      currentBatch
    };
  });

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-3 border-b flex items-center">
        <Activity size={18} className="mr-2 text-blue-600" />
        <h2 className="font-semibold">État actuel des moyens chauds</h2>
      </div>
      
      <div className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {resourcesWithStatus.map(resource => (
            <div 
              key={resource.id}
              className={`border rounded-lg p-3 ${getStatusColorClass(resource.effectiveStatus)}`}
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">{resource.name}</h3>
                <div className="flex items-center">
                  {getStatusIcon(resource.effectiveStatus)}
                </div>
              </div>
              
              <div className="text-sm mb-1">
                {getStatusLabel(resource.effectiveStatus)}
              </div>
              
              {resource.currentBatch && (
                <div className="mt-2 text-sm bg-white bg-opacity-60 rounded p-2">
                  <div className="font-medium">Fournée en cours:</div>
                  <div className="flex justify-between items-center">
                    <span>{resource.currentBatch.batchNumber}</span>
                    <span>{resource.currentBatch.cycleName}</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {resource.currentBatch.startTime} - {resource.currentBatch.endTime}
                  </div>
                </div>
              )}
              
              {resource.effectiveStatus === 'maintenance' && (
                <div className="mt-2 text-sm">
                  Retour prévu: 04/04/2025
                </div>
              )}
              
              {resource.effectiveStatus === 'error' && (
                <div className="mt-2 text-sm font-medium">
                  Intervention technique en cours
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResourceStatusPanel;